import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../Common/DataTableStyles";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useUserContext } from "../../../Contexts/UserProvider";
import { toast } from "react-toastify";
// @ts-ignore
import "react-toastify/dist/ReactToastify.css";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import AddProductDesignDialog from "./AddProductDesignDialog"
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";
import ProductTypeDetailsView from "./ProductTypeDetailView";
import { Search, Plus, MoreHorizontal, Loader2, Copy, Edit, Trash, EyeIcon, CheckIcon, Delete, RefreshCwIcon, ViewIcon, MoreVertical } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Input } from "../../../components/ui/input"
import ConfirmDialog from "../Modals/ConfirmationModal";

const ProductDesign = () => {
    const [isAddProductOpen, setIsAddProductOpen] = useState(false);
    const [catalogueTypes, setCatalogueTypes] = useState([]);
    const [searchQuery, setSearchQuery] = useState("")

    const [isLoading, setIsLoading] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);

    const [productTypeDetails, setProductTypeDetails] = useState({
        data: null,
        isShow: false
    });
    const [confirmationModal, setConfirmationModal] = useState({
        isShow: false,
        onConfirm: () => { },
    });
    const [selectedProductType, setSelectedProductType] = useState(null);
    const { getCatalogueTypes, deleteCatalougeType } = useCatalogueManagement();
    const { tenant } = useUserContext();

    const columns = useMemo(() => [
        {
            name: "Item Code",
            selector: (row) => row?.code,
            sortable: true,
        },
        {
            name: "Item Name",
            selector: (row) => row?.catalogName,
            sortable: true,
        },
        {
            name: "Description",
            selector: (row) => row?.description,
            sortable: true,
        },
        {
            name: "Fields",
            cell: (row) => {
                let metaDataFields = [];
                try {
                    const parsedFields = JSON.parse(row?.fields);
                    metaDataFields = parsedFields?.metaDataFields || [];
                } catch (e) {
                    // console.error("Error parsing fields", e);
                }

                return (
                    <div className="flex flex-wrap justify-start gap-2 py-2">
                        {metaDataFields.length > 0 ? (
                            metaDataFields.map((field, index) => (
                                <Badge
                                    key={index}
                                    variant="outline"
                                    className="bg-purple-900/30 text-purple-100 border-purple-800"
                                >
                                    {field?.fieldDisplayName || field?.fieldName}
                                </Badge>
                            ))
                        ) : (
                            <Badge
                                variant="outline"
                                className="bg-gray-800 text-gray-400 border-gray-700"
                            >
                                No Fields
                            </Badge>
                        )}
                    </div>
                );
            },
            grow: 2,
        },
        {
            name: "Actions",
            cell: (row, index) => (
                <div className="flex justify-center w-full">
                    <DropdownMenu modal={false} onOpenChange={(isOpen) => { setIsDropdownOpen(isOpen ? index : null) }} open={isDropdownOpen === index}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-black border-gray-800 text-white">
                            <DropdownMenuItem className="hover:bg-gray-800 focus:bg-gray-800" onClick={() => handleViewDetails(row, true)}>
                                <EyeIcon className="h-4 w-4 mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem className="hover:bg-gray-800 focus:bg-gray-800" onClick={() => handleUpdateType(row)}>
                                <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-gray-800" />
                            <DropdownMenuItem className="hover:bg-gray-800 focus:bg-gray-800 text-red-500" onClick={() => handleConfirmationModal(true, () => handleDeleteType(row))}>
                                <Trash className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            center: true,
        },
    ], [isDropdownOpen]);

    useEffect(() => {
        handleLoadTypes();
    }, [])

    const openAddProduct = () => {
        setSelectedProductType(null);
        setIsUpdate(false);
        setIsDropdownOpen(null);
        setIsAddProductOpen(true);
    };

    const closeAddProduct = () => {
        setIsAddProductOpen(false);
        setIsDropdownOpen(null);
        setSelectedProductType(null);
        setIsUpdate(false);
    };

    const handleLoadTypes = () => {
        setIsLoading(true);
        // Here you would typically send this data to your backend
        getCatalogueTypes(tenant?.id).then((response) => {
            setCatalogueTypes(response);
            setIsLoading(false);
        }).catch((error) => {
            console.log("Error", error);
            setIsLoading(false);
            toast.error("Failed to fetch Product Design");
        });
    };

    const handleDeleteType = (item) => {
        deleteCatalougeType(item?.id).then((res) => {
            if (isApiError(res)) {
                toast.error(getApiErrorMessage(res));
                return;
            }
            toast.success("Catalogue Type Delete Successful");
            handleLoadTypes();
        }).catch((error) => {
            toast.error(getApiErrorMessage(error));
            console.log(error);
        });
    }

    const handleUpdateType = (item) => {
        setSelectedProductType(item);
        setIsUpdate(true);
        setIsDropdownOpen(null);
        setIsAddProductOpen(true);
    }

    const handleViewDetails = (item, isOpen) => {
        setIsDropdownOpen(null);
        setProductTypeDetails(prev => ({
            data: item !== undefined && item !== null ? item : prev.data,
            isShow: isOpen
        }));
    }

    const handleConfirmationModal = (isOpen, onConfirm) => {
        setIsDropdownOpen(null);
        setConfirmationModal({
            isShow: isOpen,
            onConfirm: onConfirm,
        });
    };

    // Filter users based on search query, status, and type
    const filteredProductTypes = catalogueTypes.filter(
        (user) =>
            (user.catalogName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.code.toLowerCase().includes(searchQuery.toLowerCase())) ||
            user.description.toLowerCase().includes(searchQuery.toLowerCase())
    )


    return (
        <div className="w-full mx-auto p-6 space-y-6 product-types">
            <div className="container mx-auto p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Manage Product Design</h1>
                    <Button
                        className="bg-purple-600 hover:bg-purple-700 transition-colors"
                        onClick={openAddProduct}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Product Design
                    </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <Input
                                placeholder="Search users by email or name..."
                                className="pl-10"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
                    <DataTable
                        columns={columns}
                        data={filteredProductTypes}
                        keyField="id"
                        pagination
                        progressPending={isLoading}
                        progressComponent={
                            <div className="flex items-center justify-center p-10">
                                <Loader2 className="animate-spin h-8 w-8 text-white" />
                            </div>
                        }
                        noDataComponent={
                            <div className="text-center py-10 text-gray-400">
                                No rows to show
                            </div>
                        }
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                        responsive
                    />
                </div>

                <AddProductDesignDialog
                    isOpen={isAddProductOpen}
                    onClose={closeAddProduct}
                    selectedProductType={selectedProductType}
                    onSuccessCallBack={handleLoadTypes}
                    isUpdate={isUpdate}
                    setIsUpdate={setIsUpdate}
                />

                <ProductTypeDetailsView productType={productTypeDetails?.data} open={productTypeDetails?.isShow} onOpenChange={(isOpen) => handleViewDetails(null, isOpen)} />
            </div>

            <ConfirmDialog isShow={confirmationModal?.isShow} setIsDialogOpen={setConfirmationModal} title="Are you sure to continue ?" description="" confirmText="Delete" cancelText="Cancel" onConfirm={confirmationModal?.onConfirm} />
        </div>
    );
};

export default ProductDesign;
